import { FiRepeat } from 'react-icons/fi';

export const pastContinuousData = {
  courseId: 'past-continuous',
  courseType: 'grammar',
  title: 'Past Continuous Tense',
  icon: FiRepeat,
  overview: {
    impact: 'Setting the scene and describing background actions',
    focus: 'was/were + -ing for actions in progress in the past',
    uses: 'Speaking Part 2 narratives, Writing Task 2',
    duration: '20-25 minutes'
  },
  sections: [
    {
      title: 'Formation',
      content: `<p>Subject + <strong>was/were</strong> + verb<strong>-ing</strong></p><p>I/He/She/It <strong>was working</strong>. You/We/They <strong>were working</strong>.</p>`,
      examples: [
        {
          label: 'Background',
          text: 'It was raining when I left the house.'
        },
        {
          label: 'Simultaneous',
          text: 'She was reading while he was cooking.'
        },
        {
          label: 'Interrupted',
          text: 'I was sleeping when the phone rang.'
        }
      ]
    },
    {
      title: 'Past Simple vs Past Continuous',
      content: `<p>Often used together to tell stories:</p>`,
      points: [
        'Past continuous = background/longer action: \'I was walking home...\'',
        'Past simple = short/interrupting action: \'...when I saw an accident.\'',
        'Both continuous = simultaneous: \'While she was cooking, he was cleaning.\'',
        'Signal words: while, when, as'
      ]
    },
    {
      title: 'Using in IELTS Speaking',
      content: `<p>Past continuous makes your Speaking Part 2 stories more vivid:</p>`,
      examples: [
        {
          label: 'Basic',
          text: 'I went to the market.'
        },
        {
          label: 'Vivid',
          text: 'I was walking through the market when I noticed a beautiful painting.'
        }
      ]
    }
  ],
  exercises: [
    {
      title: 'Past Simple or Past Continuous?',
      type: 'select',
      instructions: 'Choose the correct form.',
      questions: [
        {
          text: 'I ___ (watch) TV when the lights went out.',
          options: [
            'was watching',
            'watched',
            'am watching'
          ],
          answer: 'was watching',
          explanation: 'Background action interrupted by \'went out\' → past continuous.'
        },
        {
          text: 'She ___ (arrive) at 8 pm.',
          options: [
            'arrived',
            'was arriving',
            'arriving'
          ],
          answer: 'arrived',
          explanation: 'Completed action at a specific time → past simple.'
        },
        {
          text: 'While they ___ (play), it started to rain.',
          options: [
            'were playing',
            'played',
            'are playing'
          ],
          answer: 'were playing',
          explanation: 'Background action (\'while\') → past continuous.'
        }
      ]
    }
  ],
  tips: [
    'Use past continuous for background/atmosphere in stories',
    'Combine with past simple: \'I was walking when I found...\'',
    'Makes Speaking Part 2 descriptions more vivid and detailed',
    'Signal words: while, when, as, at that time'
  ],
  dailyPractice: [
    'Practice past continuous tense concepts daily',
    'Review and apply in IELTS practice tests',
    'Write example sentences using key concepts',
    'Teach the concept to someone else to reinforce understanding'
  ],
  backPath: '/exams/english/#grammar',
  backLabel: 'Back to Grammar',
  aiContext: 'Past Continuous: was/were + -ing for background actions in the past. Used with past simple for narratives. Key for vivid IELTS Speaking Part 2 stories.'
};

export function getDataForDifficulty(difficulty = 3) {
  if (difficulty <= 2) {
    return {
      ...pastContinuousData,
      title: pastContinuousData.title + ' (Foundations)',
      overview: { ...pastContinuousData.overview, duration: '15-20 minutes' },
      sections: pastContinuousData.sections.slice(0, Math.max(2, Math.ceil(pastContinuousData.sections.length / 2))),
      exercises: pastContinuousData.exercises.slice(0, 1).map(ex => ({
        ...ex,
        title: ex.title + ' (Basic)',
        questions: ex.questions ? ex.questions.slice(0, Math.min(3, ex.questions.length)) : [],
      })),
    };
  }
  if (difficulty >= 4) {
    return {
      ...pastContinuousData,
      title: pastContinuousData.title + ' (Advanced)',
      overview: { ...pastContinuousData.overview, duration: '30-35 minutes' },
      sections: pastContinuousData.sections,
      exercises: pastContinuousData.exercises.map(ex => ({
        ...ex,
        title: ex.title + ' (Advanced)',
      })),
      tips: [
        ...(pastContinuousData.tips || []),
        'For Band 8+: Focus on natural-sounding usage rather than just accuracy.',
        'Advanced tip: Integrate this concept fluidly in timed IELTS tasks.',
      ],
    };
  }
  return pastContinuousData;
}
